import { useEffect, useCallback, useRef } from 'react';
import SignaturePad from 'signature_pad';

export const TYPED_FONT = '"Snell Roundhand", "Segoe Script", "Brush Script MT", "Apple Chancery", cursive';

export function DrawPad({ padRef, onChange, accent = 'var(--color-brand)' }) {
  const canvasRef = useRef(null);

  const resize = useCallback(() => {
    const canvas = canvasRef.current; const pad = padRef.current;
    if (!canvas || !pad) return;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    const data = pad.toData();
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    canvas.getContext('2d').scale(dpr, dpr);
    pad.clear();
    pad.fromData(data);
  }, [padRef]);

  useEffect(() => {
    const pad = new SignaturePad(canvasRef.current, { penColor: '#111', minWidth: 0.8, maxWidth: 2.6 });
    padRef.current = pad;
    const onEnd = () => onChange(!pad.isEmpty());
    pad.addEventListener('endStroke', onEnd);
    resize();
    let lastWidth = canvasRef.current.offsetWidth;
    // Mobile browsers fire resize on scroll (URL bar); only redraw on real width changes.
    const onResize = () => { const w = canvasRef.current?.offsetWidth; if (w && w !== lastWidth) { lastWidth = w; resize(); } };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); pad.removeEventListener('endStroke', onEnd); pad.off(); padRef.current = null; onChange(false); };
  }, [padRef, onChange, resize]);

  return (
    <div>
      <canvas ref={canvasRef} style={{ width: '100%', height: 170, display: 'block', background: '#fff', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', touchAction: 'none' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Sign inside the box with your finger or mouse</span>
        <button type="button" onClick={() => { padRef.current?.clear(); onChange(false); }} style={{ border: 'none', background: 'none', color: accent, fontSize: 13, cursor: 'pointer', padding: 6 }}>Clear</button>
      </div>
    </div>
  );
}

// Downscale to at most 900px wide so the upload stays well under the 500KB limit.
export function exportDrawn(pad) {
  const src = pad.canvas;
  const scale = Math.min(1, 900 / src.width);
  const out = document.createElement('canvas');
  out.width = Math.round(src.width * scale); out.height = Math.round(src.height * scale);
  out.getContext('2d').drawImage(src, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

export function renderTyped(text) {
  const canvas = document.createElement('canvas');
  canvas.width = 900; canvas.height = 220;
  const ctx = canvas.getContext('2d');
  let size = 110;
  do { ctx.font = `${size}px ${TYPED_FONT}`; size -= 4; } while (ctx.measureText(text).width > 860 && size > 24);
  ctx.fillStyle = '#111';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, 115);
  return canvas.toDataURL('image/png');
}


// Draw / type toggle plus the matching input. Parent owns the state.
export function SignatureInput({ mode, setMode, typedSig, setTypedSig, padRef, onDrawnChange, inputStyle, accent = 'var(--color-brand)' }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {[['draw', 'Draw signature'], ['type', 'Type signature']].map(([m, l]) => (
          <button key={m} type="button" onClick={() => setMode(m)} style={{
            padding: '6px 12px', fontSize: 12, borderRadius: 'var(--radius-full)', cursor: 'pointer',
            border: mode === m ? 'none' : '0.5px solid var(--color-border-medium)',
            background: mode === m ? accent : 'transparent', color: mode === m ? '#fff' : 'var(--color-text-secondary)',
          }}>{l}</button>
        ))}
      </div>
      {mode === 'draw' ? (
        <DrawPad padRef={padRef} onChange={onDrawnChange} accent={accent} />
      ) : (
        <div>
          <input style={inputStyle} value={typedSig} onChange={e => setTypedSig(e.target.value)} placeholder="Type your name" />
          <div style={{ height: 90, marginTop: 8, border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', background: '#fff', display: 'flex', alignItems: 'center', padding: '0 16px', fontFamily: TYPED_FONT, fontSize: 38, color: '#111', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {typedSig}
          </div>
        </div>
      )}
    </>
  );
}
